import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Datagrid,
  DateField,
  FunctionField,
  List,
  NumberField,
  ReferenceField,
  SimpleList,
  TextField,
  Title,
  useStore,
} from "react-admin";
import { useNavigate } from "react-router";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import CustomFilter from "features/CustomFilter";
import { MobileListRow } from "features/MobileListRow";
import dataProvider from "server/dataProvider";
import { settings } from "app";
import { studentRequests } from "api/studentApi";
import {
  APPLICATIONS,
  CURRICULUM_SELECTION_CAMPAIGN_APPLICATIONS_DETAILS,
  CURRICULUM_SELECTION_CAMPAIGNS,
  MOBILE_BREAKPOINT,
  StudentServicePath,
} from "utils/constants";
import { useNotifyError } from "utils/useNotifyError";
import VisibilityIcon from "@mui/icons-material/Visibility";

const CURRICULUM_SELECTION_CAMPAIGN_APPLICATIONS_STORE_KEY = "curriculumSelectionCampaignApplications.filter";

const ListCurriculumSelectionCampaignApplications = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const notifyError = useNotifyError();
  const navigate = useNavigate();

  const [campaignChoices, setCampaignChoices] = useState([]);
  // filters drives the <List> and the gate (persisted via useStore);
  // campaignId is the draft bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(CURRICULUM_SELECTION_CAMPAIGN_APPLICATIONS_STORE_KEY, null);
  const [campaignId, setCampaignId] = useState(filters?.campaignId ?? null);

  const getCampaigns = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(studentRequests.getCurriculumSelectionCampaignsSelect());
      setCampaignChoices(data ?? []);
    } catch (err) {
      notifyError(err);
    }
  }, [notifyError]);

  useEffect(() => {
    getCampaigns();
  }, [getCampaigns]);

  const handleSearch = () => {
    if (campaignId) {
      setFilters({ campaignId });
    }
  };

  const goToDetails = applicationId =>
    navigate(
      `/${StudentServicePath}${CURRICULUM_SELECTION_CAMPAIGN_APPLICATIONS_DETAILS}?applicationId=${applicationId}`,
      { state: { campaignId: filters?.campaignId, applicationId } },
    );

  return (
    <>
      {!filters && <Title title="Кандидатури за избор на учебен план" />}
      <CustomFilter
        title="Списък кампании"
        filters={[
          {
            label: "Кампания",
            value: campaignId,
            setValue: setCampaignId,
            type: "autocomplete",
            multiple: false,
            choices: campaignChoices,
          },
        ]}
        cardWidth={isMobile ? "350px" : "700px"}
        buttonLabel="Провери"
        buttonDisabled={!campaignId}
        buttonOnClick={handleSearch}
      />
      {!filters && (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color="secondary"
            >
              Изберете кампания, за да видите кандидатурите.
            </Typography>
          </Box>
        </Paper>
      )}
      {filters && (
        <List
          resource={`${StudentServicePath}${CURRICULUM_SELECTION_CAMPAIGNS}/${filters.campaignId}/${APPLICATIONS}`}
          exporter={false}
          title="Кандидатури за кампании за избор на учебен план"
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <MobileListRow fields={[{ label: "Факултетен номер", value: record?.facultyNumber }]} />
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Бал", value: record?.score }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Класиран учебен план",
                        value: (
                          <ReferenceField
                            source="assignedCurriculumId"
                            reference={studentRequests.getCurriculumsSelect()}
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Класиран на",
                        value: (
                          <DateField
                            source="rankedOn"
                            showTime
                            locales="bg-BG"
                          />
                        ),
                      },
                    ]}
                  />
                  <Button
                    label="Детайли"
                    startIcon={<VisibilityIcon />}
                    onClick={() => goToDetails(record?.id)}
                  />
                </div>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
            >
              <TextField
                source="facultyNumber"
                label="Факултетен номер"
                sortable={false}
              />
              <NumberField
                source="score"
                label="Бал"
                sortable={false}
              />
              <ReferenceField
                source="assignedCurriculumId"
                reference={studentRequests.getCurriculumsSelect()}
                label="Класиран учебен план"
                sortable={false}
              >
                <TextField source="name" />
              </ReferenceField>
              <DateField
                source="rankedOn"
                label="Класиран на"
                showTime
                locales="bg-BG"
                sortable={false}
              />
              <FunctionField
                render={record => (
                  <Button
                    label="Детайли"
                    startIcon={<VisibilityIcon />}
                    onClick={() => goToDetails(record?.id)}
                  />
                )}
              />
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListCurriculumSelectionCampaignApplications;
